/**
 * Human-readable chord rendering for the cheat sheet and palette rows.
 *
 * macOS renders tight glyphs (`⌘⇧P`, `⌥↓`); other platforms render `+`-joined
 * words (`Ctrl+Shift+P`, `Alt+↓`). A single symbol key that already implies
 * shift (e.g. `?`) suppresses the redundant ⇧.
 */

import type { KbdPlatform, KeyChord } from './keybinding.types';

const KEY_GLYPHS: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Enter: '⏎',
  Backspace: '⌫',
  Delete: 'Del',
  Escape: 'Esc',
  ' ': 'Space',
};

function formatKey(key: string): string {
  const glyph = KEY_GLYPHS[key];
  if (glyph) return glyph;
  return key.length === 1 ? key.toUpperCase() : key;
}

export function formatChord(chord: KeyChord, platform: KbdPlatform): string {
  const isMac = platform === 'mac';
  const symbolKey = chord.key.length === 1 && !/[a-z0-9]/i.test(chord.key);
  const parts: string[] = [];
  if (chord.mod) parts.push(isMac ? '⌘' : 'Ctrl');
  if (chord.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (chord.shift && !symbolKey) parts.push(isMac ? '⇧' : 'Shift');
  parts.push(formatKey(chord.key));
  return parts.join(isMac ? '' : '+');
}
